import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../common/types';
import styles from './SMLDeleteConfirmField.module.scss';

interface SMLDeleteConfirmFieldProps {
  name: string;
  value: string;
  isMatch: boolean;
  onChange: (value: string) => void;
}

const renderName = (chunks: React.ReactNode) => <span className={styles.name}>{chunks}</span>;

/**
 * The last gate: type the page's name.
 *
 * It is not friction for its own sake. Reading the name back forces the artist
 * to look at which page this is, which is the mistake the old "Are you sure?"
 * could not catch — it never said what it was about to delete.
 */
const SMLDeleteConfirmFieldComponent: React.FC<SMLDeleteConfirmFieldProps> = ({ name, value, isMatch, onChange }) => {
  const intl = useIntl();

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value),
    [onChange]
  );

  return (
    <div className={styles.field}>
      <span className={styles.label}>
        <FormattedMessage
          id="app.sml.delete.confirm.label"
          defaultMessage="Type <mono>{name}</mono> to confirm"
          values={{ name, mono: renderName }}
        />
      </span>
      <span className={styles.inputRow}>
        <input
          type="text"
          className={clsx(styles.input, isMatch && styles.inputMatch)}
          value={value}
          onChange={handleChange}
          autoComplete="off"
          spellCheck={false}
          aria-label={intl.formatMessage({
            id: 'app.sml.delete.confirm.aria',
            defaultMessage: 'Type the page name to confirm',
          })}
        />
        {isMatch && <FontAwesomeIcon icon={faCheck} className={styles.check} />}
      </span>
    </div>
  );
};

export const SMLDeleteConfirmField = TypedMemo(SMLDeleteConfirmFieldComponent);
